import React from 'react'
import {Link as ReactRouterLink} from 'react-router-dom'
import {RouterLink as Link} from '../../utils/chakraRouterLink'
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
} from '../../utils/chakraMonomorphic'

import {APP_VERSION} from '../../constants'
import {ALBUMS_PATH} from '../../navigation'
import {SideMenuItem} from './SideMenuItem'
import copy from './copy.json'
import {SideMenuDivider} from './SideMenuDivider'
import {CustomIcon} from '../../components'
import {useFeatureFlag} from '../../hooks/useFeatureFlag'
import {useIsCampMinderEmployee} from '../../hooks/useIsCampMinderEmployee'
import {CONFIGCAT_FLAG_KEY as CAMPANION_STAFF_ACCESS_FLAG_KEY} from '../../views/CampanionStaffAccessView/constants'
import {
  CONFIGCAT_FLAG_KEY as FR_DIAGNOSTICS_FLAG_KEY,
  FR_DIAGNOSTICS_PATH,
} from '../../views/FrDiagnosticsView/constants'
import {
  CONFIGCAT_FLAG_KEY as TAGGED_PHOTO_REPORT_FLAG_KEY,
  TAGGED_PHOTO_REPORT_PATH,
} from '../../views/TaggedPhotoReportView/constants'
import {FACIAL_RECOGNITION_PATH} from '../../views/FacialRecognitionView/constants'
import {useFacialRecognitionTabs} from '../../views/FacialRecognitionView/useFacialRecognitionTabs'

interface Props {
  isOpen: boolean
  onClose: () => void
}

// Visibility is gated by two systems on purpose:
//   - The ConfigCat flag `enable-campanion-staff-access-panel` is the
//     development rollout gate. The link is hidden until we enable it.
//   - The `feature` prop on SideMenuItem hides the link from clients
//     without the `enable-campanion-staff-engagement-features` entitlement.
// The same dual check is reflected on the route in Router.tsx.
const CampanionStaffAccessMenuItem: React.FC = () => {
  const {value: enabled} = useFeatureFlag(
    CAMPANION_STAFF_ACCESS_FLAG_KEY,
    false,
  )
  if (!enabled) return null
  return (
    <SideMenuItem
      url='/campanion-staff-access'
      feature='enable-campanion-staff-engagement-features'
      icon='add-group'>
      {copy.navigationLinks.campanionStaffAccess}
    </SideMenuItem>
  )
}

// FR Diagnostics is CampMinder-internal, so unlike the items above it is NOT
// gated on a client entitlement (SideMenuItem's `feature` prop) — a camp does
// not "have" this feature. Two gates instead:
//   - `useIsCampMinderEmployee()` hides it from every camp user.
//   - The ConfigCat flag `frPersonDiagnosticsEnabled` is the rollout gate,
//     targeted at individual CampMinder staff via the `identityEmail` custom
//     attribute while the tool is being validated.
// The same pair guards the route in Router.tsx, and the faces endpoint enforces
// the authoritative `campminderstaff` check server-side.
const FrDiagnosticsMenuItem: React.FC = () => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {value: enabled} = useFeatureFlag(FR_DIAGNOSTICS_FLAG_KEY, false)
  if (!isCampMinderEmployee || !enabled) return null
  return (
    <>
      <SideMenuDivider>{copy.navigationDividers.campminder}</SideMenuDivider>
      <SideMenuItem url={FR_DIAGNOSTICS_PATH} icon='camera'>
        {copy.navigationLinks.frDiagnostics}
      </SideMenuItem>
    </>
  )
}

// The Tagged Photo Report is CampMinder-internal on exactly the same terms as
// FR Diagnostics: no client entitlement (a camp does not "have" it), the
// employee check hides it from every camp user, and the ConfigCat flag
// `taggedPhotoReportEnabled` is the rollout gate. The same pair guards the route
// in Router.tsx, and the faces coverage endpoint enforces the authoritative
// `campminderstaff` check server-side.
//
// It renders no divider of its own — the CampMinder divider is emitted once, by
// the first internal item that is visible, so an employee who can see only one
// of the two never gets a header with nothing under it.
const TaggedPhotoReportMenuItem: React.FC<{showDivider: boolean}> = ({
  showDivider,
}) => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {value: enabled} = useFeatureFlag(TAGGED_PHOTO_REPORT_FLAG_KEY, false)
  if (!isCampMinderEmployee || !enabled) return null
  return (
    <>
      {showDivider && (
        <SideMenuDivider>{copy.navigationDividers.campminder}</SideMenuDivider>
      )}
      <SideMenuItem url={TAGGED_PHOTO_REPORT_PATH} icon='camera'>
        {copy.navigationLinks.taggedPhotoReport}
      </SideMenuItem>
    </>
  )
}

// The Facial Recognition page is Campminder-internal on the same terms: the
// employee check, plus at least one of its tab flags. The same rule guards the
// route in Router.tsx. Like the Tagged Photo Report it renders the divider only
// when no internal item above it already did.
const FacialRecognitionMenuItem: React.FC<{showDivider: boolean}> = ({
  showDivider,
}) => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {visibleTabs} = useFacialRecognitionTabs()
  if (!isCampMinderEmployee || visibleTabs.length === 0) return null
  return (
    <>
      {showDivider && (
        <SideMenuDivider>{copy.navigationDividers.campminder}</SideMenuDivider>
      )}
      <SideMenuItem url={FACIAL_RECOGNITION_PATH} icon='camera'>
        {copy.navigationLinks.facialRecognition}
      </SideMenuItem>
    </>
  )
}

// The internal items are gated on the employee check plus their own rollout
// flags, so which of them owns the divider depends on what is actually visible.
const CampMinderInternalMenuItems: React.FC = () => {
  const isCampMinderEmployee = useIsCampMinderEmployee()
  const {value: frDiagnosticsEnabled} = useFeatureFlag(
    FR_DIAGNOSTICS_FLAG_KEY,
    false,
  )
  const {value: taggedPhotoReportEnabled} = useFeatureFlag(
    TAGGED_PHOTO_REPORT_FLAG_KEY,
    false,
  )
  const frDiagnosticsVisible = isCampMinderEmployee && frDiagnosticsEnabled
  const taggedPhotoReportVisible =
    isCampMinderEmployee && taggedPhotoReportEnabled

  return (
    <>
      <FrDiagnosticsMenuItem />
      <TaggedPhotoReportMenuItem showDivider={!frDiagnosticsVisible} />
      <FacialRecognitionMenuItem
        showDivider={!frDiagnosticsVisible && !taggedPhotoReportVisible}
      />
    </>
  )
}

export const SideMenuTemplate: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => (
  <Drawer isOpen={isOpen} placement='left' onClose={onClose}>
    <DrawerOverlay />

    <DrawerContent bg='#293135' data-testid='side-menu'>
      <DrawerHeader>
        <Flex
          flexDirection='row'
          alignItems='center'
          flex='1'
          justifyContent='space-between'>
          <CustomIcon alignSelf='center' h={['70%']} w={['70%']} name='moon' />
          <DrawerCloseButton
            color='white'
            size='lg'
            position='static'
            id='side-menu-drawer-close'
          />
        </Flex>
      </DrawerHeader>

      <DrawerBody bg='#293135' p='0'>
        <Flex flexDirection='column' alignItems='flex-start' lineHeight='24px'>
          <SideMenuItem url='/home' icon='home'>
            {copy.navigationLinks.home}
          </SideMenuItem>
          <SideMenuItem
            feature='view-photo-uploader'
            url={ALBUMS_PATH}
            icon='camera'>
            {copy.navigationLinks.albums}
          </SideMenuItem>
          <SideMenuItem
            feature='texting-audience'
            url='/audiences'
            icon='people'>
            {copy.navigationLinks.audiences}
          </SideMenuItem>
          <SideMenuItem
            url='/microposts'
            icon='calendar' // used to be 'calendar'
          >
            {copy.navigationLinks.microposts}
          </SideMenuItem>
          <SideMenuItem
            url='/healthcheck'
            feature='covid-health-check'
            icon='mask'>
            {copy.navigationLinks.healthCheck}
          </SideMenuItem>
          <SideMenuItem
            url='/registration'
            feature='admin-menu-registration'
            icon='register'>
            {copy.navigationLinks.registation}
          </SideMenuItem>
          <CampanionStaffAccessMenuItem />
          <SideMenuDivider feature='texting-enabled'>Texting</SideMenuDivider>
          <SideMenuItem
            url='/integrations/eztexting'
            feature='texting-enabled'
            icon='texting'>
            {copy.navigationLinks.texting}
          </SideMenuItem>
          <SideMenuItem
            url='/formreminders'
            feature='forms-reminder'
            icon='reminder' // used to be 'reminder'
          >
            {copy.navigationLinks.formReminders}
          </SideMenuItem>
          {/* Internal tooling sits last, under its own divider, so it reads as
              separate from the camp-facing navigation. */}
          <CampMinderInternalMenuItems />
        </Flex>
      </DrawerBody>

      <DrawerFooter flexDirection='column' alignItems='flex-start' gap={2}>
        <Flex gap={4}>
          <Link
            as={ReactRouterLink}
            to='/privacy-policy'
            color='gray.500'
            fontSize='sm'
            fontWeight='normal'
            _hover={{color: 'white'}}>
            {copy.legalLinks.privacyPolicy}
          </Link>
          <Link
            as={ReactRouterLink}
            to='/terms-of-service'
            color='gray.500'
            fontSize='sm'
            fontWeight='normal'
            _hover={{color: 'white'}}>
            {copy.legalLinks.termsOfService}
          </Link>
        </Flex>
        <Box _hover={{color: 'gray.500'}}>{APP_VERSION}</Box>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
)
